import moment from 'moment-timezone';

import { currency as currencyCatalog } from '@machete/common-catalogs';

import api from '../../../lib/api';
import utils from '../../../lib/utils';

import {
  createLocalWithHolds,
  createTransfers,
  createLocalTransfers,
  createRetained,
  fixConcepts,
  fixPayments,
  createTotalTaxesPayments,
} from './getters';

function completeCfdi(store, payload = {}) {
  const {
    state,
    getters,
    rootState,
  } = store;

  const {
    isSaleNote = false,
    isRep = false,
  } = payload;

  const {
    voucherComments,
    voucherType,
    voucherConsecutive,
    voucherCurrency,
    voucherExchangeRate,
    voucherSeries,
    voucherPaymentType,
    voucherPaymentMethod,
    emitterRegime,
    emitterName,
    emitterRfc,
    receiverCfdiUsage,
    receiverName,
    receiverRfc,
    receiverFiscalResidence,
    receiverNumRegIdTrib,
    receiverZipCode,
    receiverTaxRegime,
    concepts,
    cfdisRelated,
    complement,
    amounts,
    operation,
    subjectToTax,
    exportation,
  } = state;

  const cfdisRelatedFilter = cfdisRelated.filter(item => item.uuids && item.uuids.length);

  const { decimals = 2 } = currencyCatalog.find(({ key }) => key === voucherCurrency);

  const {
    email,
    name,
    cc,
    id: customer,
  } = rootState.customers;

  const {
    zipCode,
    regime,
    rfc,
    tradeName,
    name: companyName,
  } = rootState.company;

  const correctConcepts = fixConcepts(concepts, isSaleNote, voucherCurrency);

  const transfers = createTransfers(correctConcepts);
  const retained = createRetained(correctConcepts, decimals);
  const localTransfers = createLocalTransfers(correctConcepts);
  const localWithHolds = createLocalWithHolds(correctConcepts);

  const totalTaxesTransferred = transfers.reduce(
    (accum, tax) => parseFloat(accum) + parseFloat(tax.amount || 0),
    0,
  );

  const totalLocalTaxesTransferred = localTransfers.reduce(
    (accum = 0, tax) => parseFloat(accum) + parseFloat(tax.amount),
    0,
  );

  const totalTaxesWithheld = retained.reduce(
    (accum, tax) => parseFloat(accum) + parseFloat(tax.amount),
    0,
  );

  const totalTaxesWithHolds = localWithHolds.reduce(
    (accum, tax) => parseFloat(accum) + parseFloat(tax.amount),
    0,
  );

  const totalTaxesTransferredFixed = utils.fixTotals(
    totalTaxesTransferred,
    voucherCurrency,
    decimals,
  );
  const totalLocalTaxesTransferredFixed = utils.fixTotals(
    totalLocalTaxesTransferred,
    voucherCurrency,
    decimals,
  );
  const totalTaxesWithheldFixed = utils.fixTotals(
    totalTaxesWithheld,
    voucherCurrency,
    decimals,
  );
  const totalTaxesWithHoldsFixed = utils.fixTotals(
    totalTaxesWithHolds,
    voucherCurrency,
    decimals,
  );

  const subTotal = utils.fixTotals(getters.subTotal, voucherCurrency);

  const total = subTotal
    + (totalTaxesTransferredFixed + totalLocalTaxesTransferredFixed)
    - totalTaxesWithheldFixed - totalTaxesWithHoldsFixed;

  const cfdi = {
    email,
    name,
    customer,
    cc,
    voucherExpeditionPlace: zipCode,
    voucherType,
    voucherConsecutive,
    voucherCurrency,
    voucherExchangeRate,
    voucherSeries,
    voucherPaymentType,
    voucherPaymentMethod,
    voucherDate: moment().unix(),
    voucherTotal: isSaleNote
      ? subTotal.toString()
      : utils.fixTotals(total, voucherCurrency).toString(),
    voucherSubTotal: subTotal.toString(),
    voucherComments,
    emitterRegime: regime || emitterRegime,
    emitterName: emitterName || companyName,
    emitterTradeName: tradeName,
    emitterRfc: emitterRfc || rfc,
    receiverCfdiUsage,
    receiverName,
    receiverRfc: isSaleNote ? 'XAXX010101000' : receiverRfc,
    receiverFiscalResidence,
    receiverNumRegIdTrib,
    receiverZipCode,
    receiverTaxRegime,
    concepts: correctConcepts,
    cfdisRelated: cfdisRelatedFilter,
    amounts,
    totalTaxesTransferred: utils.fixFloat(totalTaxesTransferredFixed, decimals),
    totalLocalTaxesTransferred: utils.fixFloat(totalLocalTaxesTransferredFixed, decimals),
    totalTaxesWithheld: utils.fixFloat(totalTaxesWithheldFixed, decimals),
    totalLocalTaxesWithHolds: utils.fixFloat(totalTaxesWithHoldsFixed, decimals),
    taxes: {},
    complement: { localTaxes: {} },
    expirationDate: operation.expirationDate,
    subjectToTax: subjectToTax || '02',
    exportation,
  };

  const newTransfers = transfers.reduce((accum, transfer) => {
    const {
      tax,
      factorType,
      fee,
      amount: rawAmount,
      base: rawBase,
    } = transfer;

    const newAmount = parseFloat(rawAmount);
    const amount = fee
      ? utils.fixTotals(newAmount, voucherCurrency, decimals).toFixed(decimals)
      : null;

    const newBase = parseFloat(rawBase);
    const base = utils.fixTotals(newBase, voucherCurrency, decimals).toFixed(decimals);

    const taxTransfer = {
      tax,
      factorType,
      fee,
      amount: (factorType === 'Exento') ? null : amount,
      base,
    };

    accum.push(taxTransfer);

    return accum;
  }, []);

  const newLocalTransfers = localTransfers.map((localTransfer) => {
    const {
      tax,
      factorType,
      fee,
      amount,
    } = localTransfer;

    const newAmount = parseFloat(amount);
    const fixedFeed = fee * 100;

    return {
      tax,
      factorType,
      fee: utils.fixFloat(fixedFeed, 2),
      amount: utils.fixTotals(newAmount, voucherCurrency, decimals),
    };
  });

  const newLocalWithHolds = localWithHolds.map((localWithHold) => {
    const {
      factorType,
      fee,
      amount,
      tax,
    } = localWithHold;

    let taxName = '';

    if (tax === 'cedular') {
      taxName = 'Cedular';
    } else {
      taxName = fee === '0.050000' ? '5 al millar' : '2 al millar';
    }

    const newAmount = utils.fixFloat(parseFloat(amount), decimals);
    const fixedFeed = fee * 100;

    return {
      tax: taxName,
      factorType,
      fee: utils.fixFloat(fixedFeed, 2),
      amount: utils.fixTotals(newAmount, voucherCurrency, decimals),
    };
  });

  if (newTransfers.length > 0 && !isSaleNote) cfdi.taxes.transfers = newTransfers;

  if (newLocalTransfers.length > 0 && !isSaleNote) {
    cfdi.complement.localTaxes.localTransfers = newLocalTransfers;
  }

  if (newLocalWithHolds.length > 0 && !isSaleNote) {
    cfdi.complement.localTaxes.localWithHolds = newLocalWithHolds;
  }

  if (retained.length > 0) cfdi.taxes.retained = retained;

  const allTransfersExempt = newTransfers.length > 0
    && newTransfers.every(({ factorType }) => factorType === 'Exento');

  if (newTransfers.length === 0.00 || isSaleNote || allTransfersExempt) {
    delete cfdi.totalTaxesTransferred;
  }
  if (newLocalTransfers.length === 0.00 || isSaleNote) delete cfdi.totalLocalTaxesTransferred;
  if (newLocalWithHolds.length === 0.00 || isSaleNote) delete cfdi.totalLocalTaxesWithHold;
  if (retained.length === 0.00) delete cfdi.totalTaxesWithheld;

  if (isRep) {
    delete cfdi.voucherPaymentMethod;
    delete cfdi.voucherPaymentType;
    delete cfdi.taxes;
    delete cfdi.concepts[0].taxTransfers;
    delete cfdi.concepts[0].identificationNumber;
    delete cfdi.voucherExchangeRate;

    cfdi.voucherCurrency = 'XXX';
    cfdi.voucherType = 'P';
    cfdi.receiverCfdiUsage = 'CP01';
    cfdi.complement = complement;

    cfdi.concepts[0].productServiceKey = '84111506';
    cfdi.concepts[0].quantity = '1';
    cfdi.concepts[0].unitKey = 'ACT';
    cfdi.concepts[0].description = 'Pago';
    cfdi.concepts[0].unit = '0';
    cfdi.concepts[0].amount = '0';
    cfdi.concepts[0].subjectToTax = '01';

    const correctPayments = fixPayments(cfdi.complement.payments);

    correctPayments.forEach((payment) => {
      const { taxes } = payment;

      if (taxes && Object.keys(taxes).length !== 0) {
        payment.taxes = {};

        if (taxes.transfers) {
          payment.taxes.transfers = createTransfers([taxes], 6, isRep);
        }

        if (taxes.retained) {
          payment.taxes.retained = createRetained([taxes], 6, isRep);
        }
      }

      if (payment.currency === 'MXN') {
        payment.exchangeRate = 1;
      }

      delete payment.taxesAux;
    });

    cfdi.complement.payments = correctPayments;
    cfdi.complement.totalsPayments = createTotalTaxesPayments(correctPayments, decimals);
  }

  if ((cfdi.taxes) && Object.keys(cfdi.taxes).length === 0) delete cfdi.taxes;
  if (isSaleNote) {
    cfdi.voucherType = 'Nota de remisión';
    delete cfdi.receiverFiscalResidence;
    delete cfdi.receiverCfdiUsage;
  }

  return cfdi;
}

export default {
  async save(store, payload) {
    const {
      stamp = false,
      isSaleNote = false,
      isRep = false,
    } = payload;

    const {
      state,
      commit,
      getters,
      rootState,
    } = store;

    const { haveEfirma } = rootState.company;
    const { companyId: company, subscriberId } = rootState.auth;

    const cfdi = completeCfdi({
      state,
      getters,
      rootState,
    }, {
      isSaleNote,
      isRep,
    });
    cfdi.subscriberId = subscriberId;
    const { payload: response } = await api.invoice.save(
      company,
      cfdi,
      haveEfirma ? stamp : false,
      isSaleNote,
    );

    commit('setInitialState');
    commit(
      'customers/setValue',
      { accessor: 'cc', value: null },
      { root: true },
    );

    return response;
  },

  async saveTradeTransfer(store, payload) {
    const { stamp = false } = payload;

    const {
      state,
      commit,
      getters,
      rootState,
    } = store;

    const { haveEfirma } = rootState.company;
    const { companyId: company } = rootState.auth;

    const dirtyCfdi = completeCfdi({
      state,
      getters,
      rootState,
    });

    if (dirtyCfdi.complement) delete dirtyCfdi.complement;
    if (dirtyCfdi.taxes) delete dirtyCfdi.taxes;
    if (dirtyCfdi.voucherPaymentMethod) delete dirtyCfdi.voucherPaymentMethod;
    if (dirtyCfdi.voucherPaymentType) delete dirtyCfdi.voucherPaymentType;

    const cfdi = {
      ...dirtyCfdi,
      voucherTotal: '0.00',
      voucherSubTotal: '0.00',
    };

    const { payload: response } = await api.invoice.save(
      company,
      cfdi,
      haveEfirma ? stamp : false,
      false,
    );

    commit('setInitialState');
    commit(
      'customers/setValue',
      { accessor: 'cc', value: null },
      { root: true },
    );

    return response;
  },

  async auto(store) {
    const {
      state,
      getters,
      rootState,
      commit,
    } = store;

    const { companyId: company } = rootState.auth;

    const cfdi = completeCfdi({ state, getters, rootState });

    await api.invoice.auto(company, cfdi);

    commit('setInitialState');
    commit(
      'customers/setValue',
      { accessor: 'cc', value: null },
      { root: true },
    );
  },

  async verifyAutoInvoiceToken({ commit, dispatch }, token) {
    const payload = await api.invoice.verifyAutoInvoiceToken(token);

    const {
      cfdi,
      operation,
      stamped,
      session,
    } = payload;

    const { company, ...auth } = session;

    cfdi.operation = operation;
    cfdi.operation.stamped = stamped;

    localStorage.removeItem('access_token');
    sessionStorage.setItem('access_token', session.token);

    await Promise.all(cfdi.concepts.map(concept => dispatch('ui/getCatalog', {
      catalogName: 'productServiceKey',
      search: concept.productServiceKey,
    }, { root: true })));

    commit('setValues', cfdi);

    commit(
      'auth/setValues',
      {
        ...auth,
        companyId: company.id,
        signed: true,
      },
      { root: true },
    );

    commit(
      'users/setValues',
      { email: auth.email, name: auth.name },
      { root: true },
    );

    if (company.id !== 0) {
      commit(
        'company/setValues',
        company,
        { root: true },
      );

      commit(
        'invoice/setValues',
        {
          receiverRfc: company.rfc,
          receiverName: company.name,
          receiverRegime: company.regime,
        },
        { root: true },
      );
    }

    return payload;
  },

  async getCustomerAutoInvoice({ commit, dispatch, rootState }, invoice) {
    const payload = await api.invoice.getCustomerAutoInvoice(invoice);
    const { cfdi, operation, stamped } = payload;

    cfdi.operation = operation;
    cfdi.operation.stamped = stamped;

    await Promise.all(cfdi.concepts.map(concept => dispatch('ui/getCatalog', {
      catalogName: 'productServiceKey',
      search: concept.productServiceKey,
    }, { root: true })));

    commit('setValues', cfdi);

    const { auth, company } = rootState;

    commit(
      'users/setValues',
      { email: auth.email, name: auth.name },
      { root: true },
    );

    if (company.id !== 0) {
      commit(
        'invoice/setValues',
        {
          receiverRfc: company.rfc,
          receiverName: company.name,
          receiverRegime: company.regime,
        },
        { root: true },
      );
    }

    return payload;
  },

  async updateCustomerAutoInvoice(store, { invoice, stamp }) {
    const {
      state,
      commit,
      dispatch,
      rootState,
    } = store;

    const {
      receiverCfdiUsage,
      receiverName,
      receiverRfc,
      receiverFiscalResidence,
      receiverNumRegIdTrib,
      operation: {
        name,
        email,
      },
    } = state;

    const { auth, company, customers } = rootState;

    const payload = await api.invoice.updateCustomerAutoInvoice(invoice, {
      receiverCfdiUsage,
      receiverName,
      receiverRfc,
      receiverFiscalResidence,
      receiverNumRegIdTrib,
      name,
      email,
      taxRegime: customers.taxRegime,
      zipCode: customers.zipCode,
    }, stamp);

    const { cfdi, operation, stamped } = payload;

    cfdi.operation = operation;
    cfdi.operation.stamped = stamped;

    await Promise.all(cfdi.concepts.map(concept => dispatch('ui/getCatalog', {
      catalogName: 'productServiceKey',
      search: concept.productServiceKey,
    }, { root: true })));
    commit('setValues', cfdi);

    commit(
      'users/setValues',
      { email: auth.email, name: auth.name },
      { root: true },
    );

    if (company.id !== 0) {
      commit(
        'invoice/setValues',
        {
          receiverRfc: company.rfc,
          receiverName: company.name,
          receiverRegime: company.regime,
        },
        { root: true },
      );
    }

    commit('setInitialState');

    return payload;
  },

  async getByUuid({ commit, dispatch }, uuid) {
    const payload = await api.invoice.getByUuid(uuid);

    const {
      cfdi,
      operation,
      customer,
      stamped,
      debt,
    } = payload;

    cfdi.operation = operation;
    cfdi.operation.stamped = stamped;
    cfdi.operation.debt = debt;
    cfdi.concepts = cfdi.concepts.map((tax, index) => {
      if (!Array.isArray(tax.retainedTaxes)) tax.retainedTaxes = [];
      if (!Array.isArray(tax.taxTransfers)) tax.taxTransfers = [];
      tax.id = index + 1;

      return tax;
    });

    const cleanProdServKey = cfdi.concepts.reduce((accum, { productServiceKey }) => {
      if (!accum.includes(productServiceKey)) accum.push(productServiceKey);
      return accum;
    }, []);

    const catalogsPromises = cleanProdServKey.map(productServiceKey => dispatch('ui/getCatalog', {
      catalogName: 'productServiceKey',
      search: productServiceKey,
    }, { root: true }));

    catalogsPromises.push(dispatch('customers/getSingle', operation.customer, { root: true }));

    await Promise.all(catalogsPromises);

    commit('setValues', cfdi);

    commit(
      'users/setValues',
      {
        email: operation.isSalaryReceipt ? operation.email : customer.email,
        name: operation.isSalaryReceipt ? operation.name : customer.name,
      },
      { root: true },
    );

    return payload;
  },

  async updateByUuid(store, payload) {
    const {
      state,
      getters,
      rootState,
      commit,
    } = store;

    const { haveEfirma } = rootState.company;
    const { subscriberId } = rootState.auth;

    const { uuid, stamp = false, isSaleNote } = payload;

    const cfdi = completeCfdi(
      { state, getters, rootState },
      { isSaleNote, stamp: haveEfirma ? stamp : false },
    );
    cfdi.subscriberId = subscriberId;
    const response = await api.invoice.updateByUuid(uuid, cfdi, haveEfirma ? stamp : false);

    commit('setInitialState');

    return response;
  },

  async updateTradeTransfer(store, payload) {
    const { stamp = false, uuid } = payload;

    const {
      state,
      commit,
      getters,
      rootState,
    } = store;

    const { haveEfirma } = rootState.company;

    const dirtyCfdi = completeCfdi({
      state,
      getters,
      rootState,
    });

    if (dirtyCfdi.cfdisRelated) delete dirtyCfdi.cfdisRelated;
    if (dirtyCfdi.complement) delete dirtyCfdi.complement;
    if (dirtyCfdi.taxes) delete dirtyCfdi.taxes;
    if (dirtyCfdi.voucherPaymentMethod) delete dirtyCfdi.voucherPaymentMethod;
    if (dirtyCfdi.voucherPaymentType) delete dirtyCfdi.voucherPaymentType;

    const cfdi = {
      ...dirtyCfdi,
      voucherTotal: '0.00',
      voucherSubTotal: '0.00',
    };

    const { payload: response } = await api.invoice.updateByUuid(
      uuid,
      cfdi,
      haveEfirma ? stamp : false,
    );

    commit('setInitialState');

    return response;
  },

  async cancelByUuid({ dispatch, rootState }, uuid) {
    const payload = await api.invoice.cancelByUuid(uuid);

    const { queries } = rootState.invoices;

    dispatch('invoices/getAll', queries, { root: true });

    return payload;
  },

  async cancelSaleNoteByUuid(store, uuid) {
    const payload = await api.invoice.cancelSaleNoteByUuid(uuid);

    return payload;
  },
  async cancelSalaryReceiptByUuid(store, uuid) {
    const payload = await api.invoice.cancelSaleNoteByUuid(uuid);

    return payload;
  },
  async getAsset(store, { type, uuid, customerDownload = false }) {
    const fileUrl = await api.invoice.getAsset(type, uuid, customerDownload);
    const payload = await api.invoice.downloadFile(fileUrl, customerDownload);

    const body = document.querySelector('body');

    const download = document.createElement('a');
    const [path] = fileUrl.split('/').reverse();
    const [file] = path.split('?');

    const downloadUrl = URL.createObjectURL(payload);

    download.setAttribute('href', downloadUrl);
    download.setAttribute('download', file);
    download.style.display = 'none';

    body.appendChild(download);

    download.click();

    return fileUrl;
  },
  async resendAssetsEmail(store, uuid) {
    const payload = await api.invoice.resendAssetsEmail(uuid);

    return payload;
  },
  async deleteByUuid(store, uuid) {
    const payload = await api.invoice.deleteByUuid(uuid);

    return payload;
  },

  async getBanner(rootState, position) {
    const payload = await api.invoice.getBanner(position, 'autoInvoicePortal');
    return payload;
  },
  async regeneratePdf(store, body) {
    const payload = await api.invoice.regeneratePdf(body);
    return payload;
  },
  async downloadStatusAccount(store, { companyId, customerId }) {
    const payload = await api.invoices.getStatusAccountCsv(companyId, customerId);
    const body = document.querySelector('body');

    const download = document.createElement('a');
    const downloadUrl = URL.createObjectURL(payload);

    download.setAttribute('href', downloadUrl);
    download.setAttribute('download', 'estado_de_cuenta.csv');
    download.style.display = 'none';

    body.appendChild(download);

    download.click();

    return 'estado_de_cuenta.csv';
  },

  async cfdiStatus({ dispatch, rootState }, invoice) {
    const { uuid } = invoice.complement.digitalTaxStamp;

    await api.invoice.updateCfdiStatus(uuid);

    const { queries } = rootState.invoices;

    dispatch('invoices/getAll', queries, { root: true });
  },

  async updateCancellation(store, { uuid, cancellation }) {
    const payload = await api.invoice.updateCancellation(uuid, cancellation);

    return payload;
  },
};
